import type { FC, ReactNode } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Fade, IconButton, Typography, useTheme } from '@mui/material'
import {
  Add as AddIcon,
  ChevronLeft,
  ChevronRight,
  Close as CloseIcon,
  FirstPage,
  LastPage,
  MoreHoriz,
  OpenInFull,
  South,
} from '@mui/icons-material'
import type { AgentMode } from '@/index'
import type { FormattedAgentActivity } from '@/features/events/activity/utils/types'
import { AgentModeBadge } from '@/features/events/conversation/agent-mode-badges/AgentModeBadge'
import { AgentModeBadgeTransition } from '@/features/events/conversation/agent-mode-badges/AgentModeBadgeTransition'
import { ZoomControls } from '@/features/events/conversation/controls/components/ZoomControls'
import { ActivityFeedContent } from '@/features/events/activity/components/ActivityFeedContent'
import { ActivityStatusIndicator } from '@/features/events/activity/components/ActivityStatusIndicator'
import { IdleActivityIndicator } from '@/features/events/activity/components/IdleActivityIndicator'
import type { DemoStatusIconConfig } from '../data/mockConversationScript'
import { DemoStatusIcon } from './DemoStatusIcon'
import { DemoPauseControl } from './DemoPauseControl'

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

interface DemoConversationChromeProps {
  readonly agentMode: AgentMode
  readonly agentModeTransition: { readonly from: AgentMode; readonly to: AgentMode } | undefined
  readonly statusIcon: DemoStatusIconConfig
  readonly showPause: boolean
  readonly showInject: boolean
  readonly activity: FormattedAgentActivity | null
  readonly children: ReactNode
  readonly frameKey: number
}

export const DemoConversationChrome: FC<DemoConversationChromeProps> = ({
  agentMode,
  agentModeTransition,
  statusIcon,
  showPause,
  showInject,
  activity,
  children,
  frameKey,
}) => {
  const theme = useTheme()

  return (
    <Box sx={chromeContainerSx}>
      {/* Title row: badge + title + breadcrumb (left) | window controls (right) */}
      <Box sx={titleRowSx}>
        <Box sx={titleContentSx}>
          <Box sx={badgeWrapperSx}>
            {agentModeTransition ? (
              <AgentModeBadgeTransition
                from={agentModeTransition.from}
                to={agentModeTransition.to}
              />
            ) : (
              <AgentModeBadge agentMode={agentMode} />
            )}
          </Box>

          <Typography variant="h6" sx={titleSx}>
            Conversation
          </Typography>

          <Typography variant="body2" sx={breadcrumbSx}>
            MacBook Pro &gt; Nexus Web &gt; Implement OAuth2
          </Typography>
        </Box>

        <Box sx={windowControlsContainerSx}>
          <IconButton size="small" sx={scrollToBottomButtonSx} tabIndex={-1} aria-hidden="true">
            <South fontSize="small" />
          </IconButton>
          <IconButton size="small" sx={expandButtonSx} tabIndex={-1} aria-hidden="true">
            <OpenInFull fontSize="small" />
          </IconButton>
          <IconButton size="small" sx={closeButtonSx(theme)} tabIndex={-1} aria-hidden="true">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Controls row: zoom | pause + status + inject | pagination */}
      <Box sx={controlsRowSx}>
        <Box sx={controlsRowLeftSx}>
          <Box sx={pointerEventsNoneSx}>
            <ZoomControls zoomLevel={1} onZoomIn={noop} onZoomOut={noop} onZoomReset={noop} />
          </Box>
        </Box>

        <Box sx={controlsRowCenterSx}>
          <DemoPauseControl visible={showPause} />
          <DemoStatusIcon config={statusIcon} />
          {showInject && (
            <Box sx={injectSlotSx}>
              <AddIcon sx={injectIconSx(theme)} />
            </Box>
          )}
        </Box>

        <Box sx={controlsRowRightSx}>
          <IconButton size="small" sx={paginationButtonSx} tabIndex={-1} aria-hidden="true">
            <FirstPage fontSize="small" />
          </IconButton>
          <IconButton size="small" sx={paginationButtonSx} tabIndex={-1} aria-hidden="true">
            <ChevronLeft fontSize="small" />
          </IconButton>
          <MoreHoriz fontSize="small" sx={paginationDotsColorSx} />
          <IconButton size="small" sx={paginationButtonSx} tabIndex={-1} aria-hidden="true">
            <ChevronRight fontSize="small" />
          </IconButton>
          <IconButton size="small" sx={paginationButtonSx} tabIndex={-1} aria-hidden="true">
            <LastPage fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Status row: activity feed (centered) */}
      <Box sx={statusRowSx}>
        {activity ? (
          <Box sx={activityContentSx}>
            <Box sx={activityDotSlotSx}>
              <ActivityStatusIndicator status={activity.status} />
            </Box>
            <ActivityFeedContent activity={activity} />
          </Box>
        ) : (
          <IdleActivityIndicator />
        )}
      </Box>

      {/* Content area: fixed height with fade transition */}
      <Box sx={contentAreaSx}>
        <Fade key={frameKey} in={true} timeout={200}>
          <Box sx={contentInnerSx}>{children}</Box>
        </Fade>
      </Box>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const noop = () => {}

// ---------------------------------------------------------------------------
// Styles — matching prod BaseDialog / SessionDialog layout
// ---------------------------------------------------------------------------

const chromeContainerSx: SxProps<Theme> = (theme) => ({
  borderRadius: 2,
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.08)'
      : '1px solid rgba(62, 39, 35, 0.10)',
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 4px 24px rgba(0, 0, 0, 0.3)'
      : '0 4px 24px rgba(0, 0, 0, 0.08)',
  overflow: 'hidden',
  backgroundColor: 'background.paper',
  width: '100%',
  maxWidth: 1000,
})

const titleRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  px: 3,
  pt: 2.5,
  pb: 0,
  mb: 3,
}

const titleContentSx: SxProps<Theme> = {
  flex: 1,
  pr: 2,
}

const badgeWrapperSx: SxProps<Theme> = {
  mb: 2,
}

const titleSx: SxProps<Theme> = {
  fontWeight: 600,
  fontSize: '1.25rem',
  mb: 1,
}

const breadcrumbSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontSize: '0.875rem',
  fontWeight: 400,
  lineHeight: 1.5,
}

const windowControlsContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 3,
}

const scrollToBottomButtonSx: SxProps<Theme> = {
  color: 'text.secondary',
  cursor: 'default',
  '&:hover': { backgroundColor: 'transparent' },
}

const expandButtonSx: SxProps<Theme> = {
  color: 'text.secondary',
  cursor: 'default',
  '&:hover': { backgroundColor: 'transparent' },
}

const closeButtonSx = (theme: Theme): SxProps<Theme> => ({
  color: theme.palette.error.main,
  width: 32,
  height: 32,
  cursor: 'default',
  '&:hover': { backgroundColor: 'transparent' },
})

const controlsRowSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  position: 'relative',
  px: 3,
}

const controlsRowLeftSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
}

const controlsRowCenterSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
}

const controlsRowRightSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
}

const injectSlotSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '4px',
}

const injectIconSx = (theme: Theme): SxProps<Theme> => ({
  fontSize: '1.3rem',
  color: theme.palette.info.main,
})

const paginationButtonSx: SxProps<Theme> = {
  color: 'text.secondary',
  cursor: 'default',
  '&:hover': { backgroundColor: 'transparent' },
}

const pointerEventsNoneSx: SxProps<Theme> = {
  pointerEvents: 'none',
}

const paginationDotsColorSx: SxProps<Theme> = {
  color: 'text.disabled',
}

const statusRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  px: 3,
  pt: 5,
  pb: 2,
  borderBottom: '1px solid',
  borderColor: 'divider',
}

const activityContentSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
}

const activityDotSlotSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  flexShrink: 0,
}

const contentAreaSx: SxProps<Theme> = {
  height: 680,
  overflow: 'hidden',
  position: 'relative',
  px: 3,
  py: 2,
}

const contentInnerSx: SxProps<Theme> = {
  height: '100%',
  overflow: 'hidden',
  fontSize: '0.9rem',
  '& *': { cursor: 'default' },
}
